/*
 * KOTT Lab — Custom cookie consent
 *
 * Hooks into Shopify's Customer Privacy (consent-tracking) API to gate tracking.
 * - Shows a bottom banner only when Shopify says a choice is needed (shouldShowBanner()).
 * - Records choices via customerPrivacy.setTrackingConsent(); we never persist our own cookie.
 * - The footer "הגדרות קוקיז" link ([data-cookie-preferences]) reopens the preferences modal at any time.
 *
 * sale_of_data is always false (not relevant to the Israeli market, no user-facing toggle).
 */
(function () {
  'use strict';

  var FEATURE = [{ name: 'consent-tracking-api', version: '0.1' }];
  var CATEGORIES = ['analytics', 'marketing', 'preferences'];
  var MIRROR_KEY = 'kott:cookie-consent';

  /*
   * localStorage mirror of the visitor's choice. The Customer Privacy API stays
   * the source of truth, but its cookie does not persist on theme-dev previews
   * (and can be cleared independently) — the mirror lets us re-apply the choice
   * instead of re-asking.
   */
  function readMirror() {
    try {
      var raw = window.localStorage.getItem(MIRROR_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed !== null ? parsed : null;
    } catch (err) {
      return null;
    }
  }

  function writeMirror(consent) {
    try {
      window.localStorage.setItem(MIRROR_KEY, JSON.stringify({
        analytics: !!consent.analytics,
        marketing: !!consent.marketing,
        preferences: !!consent.preferences,
        ts: Date.now(),
      }));
    } catch (err) {
      /* storage unavailable — fall back to API-only behaviour */
    }
  }

  var root = document.getElementById('cookie-consent');
  if (!root) return;

  var banner = root.querySelector('[data-cookie-banner]');
  var dialogComponent = root.querySelector('dialog-component');

  var privacyReady = false;
  /** Pending callbacks waiting for the Customer Privacy API to load. */
  var pending = [];

  /**
   * Resolve window.Shopify.customerPrivacy, loading the consent-tracking feature first.
   * Calls back with the API object, or null if it could not be loaded.
   */
  function withPrivacy(callback) {
    if (privacyReady && window.Shopify && window.Shopify.customerPrivacy) {
      callback(window.Shopify.customerPrivacy);
      return;
    }

    pending.push(callback);
    if (pending.length > 1) return; // load already in flight

    var attempts = 0;
    (function ensureShopify() {
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures(FEATURE, function (error) {
          var api = window.Shopify && window.Shopify.customerPrivacy;
          if (error || !api) {
            // eslint-disable-next-line no-console
            console.warn('[cookie-consent] Customer Privacy API unavailable', error);
            flush(null);
            return;
          }
          privacyReady = true;
          flush(api);
        });
        return;
      }
      if (attempts++ > 50) { // ~10s ceiling
        flush(null);
        return;
      }
      setTimeout(ensureShopify, 200);
    })();
  }

  function flush(api) {
    var callbacks = pending.slice();
    pending.length = 0;
    callbacks.forEach(function (cb) {
      try {
        cb(api);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[cookie-consent]', err);
      }
    });
  }

  /* ---------- Banner ---------- */

  function showBanner() {
    if (!banner) return;
    banner.hidden = false;
    // Lift the floating buttons out of the way on mobile (they share the bottom edge).
    document.body.classList.add('cookie-consent-banner-open');
    // Allow the element to lay out before transitioning in (no layout shift on load).
    requestAnimationFrame(function () {
      banner.classList.add('is-visible');
    });
  }

  function hideBanner() {
    document.body.classList.remove('cookie-consent-banner-open');
    if (!banner || banner.hidden) return;
    banner.classList.remove('is-visible');
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      banner.hidden = true;
      return;
    }
    var done = function () {
      banner.hidden = true;
      banner.removeEventListener('transitionend', done);
    };
    banner.addEventListener('transitionend', done);
  }

  /* ---------- Consent writes ---------- */

  function consentObject(analytics, marketing, preferences) {
    return {
      analytics: !!analytics,
      marketing: !!marketing,
      preferences: !!preferences,
      sale_of_data: false,
    };
  }

  function setConsent(consent, after) {
    writeMirror(consent);
    withPrivacy(function (api) {
      if (!api) {
        hideBanner();
        closeModal();
        if (after) after();
        return;
      }
      api.setTrackingConsent(consent, function () {
        hideBanner();
        closeModal();
        if (after) after();
      });
    });
  }

  function acceptAll() {
    setConsent(consentObject(true, true, true));
  }

  function declineAll() {
    setConsent(consentObject(false, false, false));
  }

  /* ---------- Preferences modal ---------- */

  function toggleFor(category) {
    return root.querySelector('[data-consent-toggle="' + category + '"]');
  }

  /**
   * Has the visitor already made a consent decision Shopify has persisted?
   * currentVisitorConsent() returns '' for "no decision" and 'yes'/'no' once chosen.
   * We check this in addition to shouldShowBanner() because, with the built-in banner
   * removed and the Israel consent region enabled, shouldShowBanner() stays true even
   * after a choice — it signals "a banner is warranted in this region", not "undecided".
   */
  function hasConsentDecision(api) {
    try {
      var current = (api && api.currentVisitorConsent && api.currentVisitorConsent()) || {};
      return ['analytics', 'marketing', 'preferences', 'sale_of_data'].some(function (key) {
        return current[key] === 'yes' || current[key] === 'no';
      });
    } catch (err) {
      return false;
    }
  }

  /** Prefill the toggles from the visitor's current consent (if any). */
  function syncToggles(api) {
    var current = {};
    try {
      current = (api && api.currentVisitorConsent && api.currentVisitorConsent()) || {};
    } catch (err) {
      current = {};
    }
    var mirror = readMirror();
    CATEGORIES.forEach(function (category) {
      var input = toggleFor(category);
      if (!input) return;
      if (current[category] === 'yes' || current[category] === 'no') {
        input.checked = current[category] === 'yes';
      } else if (mirror) {
        input.checked = !!mirror[category];
      } else {
        input.checked = false;
      }
    });
  }

  function openModal() {
    withPrivacy(function (api) {
      syncToggles(api);
      if (!dialogComponent) return;
      var open = function () {
        if (typeof dialogComponent.showDialog === 'function') dialogComponent.showDialog();
      };
      if (window.customElements && customElements.whenDefined) {
        customElements.whenDefined('dialog-component').then(open);
      } else {
        open();
      }
    });
  }

  function closeModal() {
    if (dialogComponent && typeof dialogComponent.closeDialog === 'function' && dialogComponent.querySelector('dialog')?.open) {
      dialogComponent.closeDialog();
    }
  }

  function savePreferences() {
    setConsent(
      consentObject(
        toggleFor('analytics') && toggleFor('analytics').checked,
        toggleFor('marketing') && toggleFor('marketing').checked,
        toggleFor('preferences') && toggleFor('preferences').checked
      )
    );
  }

  function modalAcceptAll() {
    CATEGORIES.forEach(function (category) {
      var input = toggleFor(category);
      if (input) input.checked = true;
    });
    acceptAll();
  }

  function modalDeclineAll() {
    CATEGORIES.forEach(function (category) {
      var input = toggleFor(category);
      if (input) input.checked = false;
    });
    declineAll();
  }

  /* ---------- Wiring ---------- */

  function on(action, handler) {
    root.querySelectorAll('[data-cookie-action="' + action + '"]').forEach(function (el) {
      el.addEventListener('click', function (event) {
        event.preventDefault();
        handler();
      });
    });
  }

  on('accept', acceptAll);
  on('decline', declineAll);
  on('manage', openModal);
  on('save', savePreferences);
  on('accept-all', modalAcceptAll);
  on('decline-all', modalDeclineAll);

  // Footer link (or any element) can open preferences from anywhere on the page.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-cookie-preferences]');
    if (!trigger) return;
    event.preventDefault();
    openModal();
  });

  /* ---------- Init ---------- */

  withPrivacy(function (api) {
    if (!api || typeof api.shouldShowBanner !== 'function') return;
    try {
      // Show only when a banner is warranted in this region AND the visitor
      // hasn't already chosen. The decision check is what actually keeps the
      // banner from reappearing on every reload (shouldShowBanner stays true).
      if (!api.shouldShowBanner() || hasConsentDecision(api)) return;
      var mirror = readMirror();
      if (mirror) {
        // The API lost the decision (e.g. theme-dev preview) but we have it —
        // re-apply silently instead of asking again.
        api.setTrackingConsent(
          consentObject(mirror.analytics, mirror.marketing, mirror.preferences),
          function () {}
        );
        return;
      }
      showBanner();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[cookie-consent] shouldShowBanner failed', err);
    }
  });
})();
